/**
 * `combo.ts` — **set menu / paket**: produk yang menjual produk lain.
 *
 * ### ⭐ Bentuknya diturunkan dari data VTEC nyata, bukan dari selera
 * `raw_pos_orderdetail` Arena, 6 bulan:
 *
 * ⚠️ **KOREKSI (2026-08-01).** Versi sebelumnya mengutip 5.633.477 baris `productsettype = 14`
 * sebagai "komponen set menu". **Salah** — verifikasi sampel menunjukkan itu baris
 * **modifier/komentar** (`producttype 14 = Modifier`, semuanya `iscomment = 1`, sebagian bahkan
 * tanpa produk). Bukti combo yang sahih adalah analisis `productcomponentgroup` di teardown §8d:
 * **7.538 grup**, semantik `min`/`max`, dan kesimpulan bahwa **grup dengan 1 opsi & min=max=1
 * ITULAH kasus statik** — bukan konsep terpisah.
 *
 * ⇒ **Induk memegang harga, komponen nol** — bentuk yang dipakai VTEC dan yang harus tetap benar
 * saat dual-run. Yang berubah hanyalah angka yang saya pakai untuk mendukungnya.
 *
 * ### ⛔ Tapi "nol di tagihan" ≠ "nol di laporan"
 * Kalau komponen benar-benar nol di mana-mana, laporan per departemen akan menyatakan **dapur tidak
 * memasak apa-apa** setiap kali tamu memesan set menu — sementara `department` justru ada untuk
 * menjawab pertanyaan itu. Karena itu komponen menyimpan `allocated_price`: **tidak pernah
 * ditagihkan**, hanya untuk laporan, dan Σ-nya **eksak** sama dengan harga induk (`allocateMoney`,
 * sisa-terbesar).
 *
 * @module
 */
/**
 * Jenis produk. `combo` = produk yang komposisinya produk lain.
 *
 * ⚠️ Ini enum, bukan boolean `is_combo`. Bukti kenapa: satu boolean di VTEC (`asItemDiscount`)
 * memikul tiga kategori ekonomi sekaligus dan menyembunyikan beban Rp12,9jt + liabilitas Rp27,9M
 * + piutang Rp9,5M. Jenis produk akan bertambah (`weighed`, `voucher`); boolean tak bisa tumbuh.
 */
export const PRODUCT_KINDS = Object.freeze(['simple', 'combo']);
const KIND_SET = new Set(PRODUCT_KINDS);
/** Guard {@link ProductKind}. */
export function isProductKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
/**
 * Peran sebuah baris pesanan.
 *
 * ⭐ **`combo_component` adalah baris SUNGGUHAN**, bukan teks keterangan di struk. Ia harus baris
 * karena tiga hal memerlukannya secara terpisah: **tiket dapur** (stasiun berbeda), **stok**
 * (resep komponen), dan **laporan** (departemen komponen). Menjadikannya catatan teks berarti
 * ketiganya kehilangan datanya sekaligus.
 */
export const LINE_KINDS = Object.freeze([
    'item',
    'combo_parent',
    'combo_component',
    /**
     * ⭐ Modifier/condiment (§5d) — **berbeda dari komponen combo**: ia MENAMBAH harga baris
     * induknya, jadi ia menanggung pajak. Komponen combo justru dipaksa berharga nol.
     */
    'modifier',
]);
const LINE_KIND_SET = new Set(LINE_KINDS);
/** Guard {@link LineKind}. */
export function isLineKind(v) {
    return typeof v === 'string' && LINE_KIND_SET.has(v);
}
/**
 * Apakah baris ini menanggung harga & pajak?
 *
 * ⛔ Satu-satunya jawaban yang benar: **komponen tidak pernah**. Kalau komponen ikut menghasilkan
 * baris pajak, pajaknya terhitung dua kali — sekali di induk (yang memegang harga) dan sekali lagi
 * di komponen. Invarian §8 `tax_total = Σ baris` akan tetap "benar" secara aritmetika, tapi
 * angkanya salah, dan itu jenis kesalahan yang lolos semua pemeriksaan penjumlahan.
 */
export function lineCarriesPrice(kind) {
    return kind !== 'combo_component';
}
/**
 * ⭐ **Kontrol yang MEMAKSA (P6), bukan sekadar mencatat.**
 *
 * Set menu tanpa komponen wajibnya adalah struk yang menagih paket tapi dapur tak pernah tahu
 * harus memasak apa — dan tamu yang menerima piring kurang. Pemeriksaan ini menutupnya sebelum
 * baris tersimpan, bukan sesudah struk tercetak.
 */
export function validateComboSelections(rules, selections) {
    const out = [];
    const perGrup = new Map();
    for (const s of selections) {
        const list = perGrup.get(s.groupCode);
        if (list === undefined)
            perGrup.set(s.groupCode, [s]);
        else
            list.push(s);
    }
    // Grup yang tak dikenal: ditolak, bukan diabaikan. Mengabaikannya berarti pilihan tamu hilang
    // diam-diam dan tak ada yang tahu sampai piringnya salah.
    const ruleCode = new Set(rules.map((r) => r.groupCode));
    for (const code of perGrup.keys()) {
        if (!ruleCode.has(code)) {
            out.push({ groupCode: code, reason: 'unknown_group', detail: `grup "${code}" bukan milik combo ini` });
        }
    }
    for (const r of rules) {
        const selected = perGrup.get(r.groupCode) ?? [];
        for (const s of selected) {
            if (!r.allowed.includes(s.productCode)) {
                out.push({
                    groupCode: r.groupCode,
                    reason: 'not_allowed',
                    detail: `"${s.productCode}" bukan pilihan di grup "${r.groupCode}"`,
                });
            }
        }
        if (selected.length < r.minSelect) {
            out.push({
                groupCode: r.groupCode,
                reason: 'too_few',
                detail: `grup "${r.groupCode}" butuh minimal ${String(r.minSelect)}, terpilih ${String(selected.length)}`,
            });
        }
        if (selected.length > r.maxSelect) {
            out.push({
                groupCode: r.groupCode,
                reason: 'too_many',
                detail: `grup "${r.groupCode}" maksimal ${String(r.maxSelect)}, terpilih ${String(selected.length)}`,
            });
        }
    }
    return out;
}
//# sourceMappingURL=combo.js.map